import React, { useContext, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Bug, ChevronLeft, ChevronRight, Download, Lightbulb, Loader, Plus, Search, Settings2 } from "lucide-react";
import { saveAs } from "file-saver";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Breadcrumbs from "~/components/Breadcrumbs";
import PageHeader from "~/components/PageHeader";
import PreviewController from "~/components/PreviewController";
import { C, MODULES, STATUS, StatusChip, formatDate } from "~/components/SupportChat/tickets";
import { getCurrentPage } from "~/functions";
import { PageContext } from "~/App";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import api from "~/services/api";
import { FullGridContext } from "..";
import PagePreview from "./Preview";

export function TicketsLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { pages } = useContext(PageContext);
  const paths = pathname.split("/");
  const redirectRouteName = pages
    .find((page) => page.name === paths[1])
    .children.find((page) => page.path === `/${paths[1]}/${paths[2]}`)
    .children[0].path;
  useEffect(() => {
    if (pathname.toUpperCase() === `/${paths[1]}/${paths[2]}`.toUpperCase()) {
      navigate(redirectRouteName);
    }
  }, [pathname]);
  return <Outlet />;
}

export const findAccess = (nodes = [], alias) => {
  for (const node of nodes) {
    if (node.alias === alias) return node;
    const found = findAccess(node.children, alias);
    if (found) return found;
  }
  return undefined;
};

const SUMMARY = ["pending_approval", "changes_requested", "approved", "in_progress", "closed", "rejected"];
const PALETTE = {
  pending_approval: "#f59e0b",
  changes_requested: "#fb923c",
  approved: "#22c55e",
  in_progress: "#3b82f6",
  closed: "#64748b",
  rejected: "#ef4444",
};

const toISODate = (date) => {
  const d = new Date(date);
  d.setHours(12, 0, 0, 0);
  return d.toISOString().slice(0, 10);
};
const monthStart = () => {
  const now = new Date();
  return toISODate(new Date(now.getFullYear(), now.getMonth(), 1));
};
const monthEnd = () => {
  const now = new Date();
  return toISODate(new Date(now.getFullYear(), now.getMonth() + 1, 0));
};

const SmallStatCard = ({ label, value, color, active, onClick }) => (
  <div
    className={`flex flex-col items-center px-4 py-2 cursor-pointer rounded-md transition-colors ${active ? "bg-gray-100 ring-1 ring-gray-300" : "hover:bg-gray-50"}`}
    onClick={onClick}
  >
    <span className="text-[11px] text-gray-500 uppercase tracking-wide mb-1">{label}</span>
    <span className="text-2xl font-bold" style={{ color: color || "#1f2937" }}>{value}</span>
  </div>
);

const dateInput = "rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-mila_orange";

const SCOPES = [
  { value: "", label: "All" },
  { value: "awaiting", label: "Awaiting approval" },
  { value: "mine", label: "Mine" },
];
const selectClass = "text-xs border rounded-md px-2 py-2 bg-white text-gray-700 outline-none";
const moduleLabel = (value) => MODULES.find((m) => m.value === value)?.label || value;

export default function Tickets() {
  const currentPage = getCurrentPage();
  const { accessModule, opened, setOpened } = useContext(FullGridContext);
  const pageAccess = findAccess(accessModule.children, "tickets-list") || findAccess(accessModule.children, "tickets");
  const [me, setMe] = useState({ holding: false, approver: false, level: 0 });
  const [summary, setSummary] = useState({ status: {}, module: {}, type: {} });
  const [filters, setFilters] = useState({ search: "", status: "", type: "", module: "", scope: "" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ rows: [], totalRows: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const filialId = useSelector((state) => state.auth.filial?.id);
  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(monthEnd);

  const setFilter = (key, value) => {
    setPage(1);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  useEffect(() => {
    api.get("/tickets/settings").then(({ data: settings }) => setMe(settings.me)).catch(() => {});
  }, []);

  useEffect(() => {
    const onCreated = (event) => {
      setFilters({ search: "", status: "", type: "", module: "", scope: "" });
      setFromDate(monthStart());
      setToDate(monthEnd());
      setPage(1);
      setOpened(event.detail);
    };
    window.addEventListener("ticket-created", onCreated);
    return () => window.removeEventListener("ticket-created", onCreated);
  }, []);

  useEffect(() => {
    setReload((n) => n + 1);
  }, [opened]);

  useEffect(() => {
    api
      .get("/tickets/summary", { params: { from_date: fromDate, to_date: toDate } })
      .then(({ data: counts }) => setSummary(counts))
      .catch(() => {});
  }, [reload, fromDate, toDate, filialId]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const timer = setTimeout(() => {
      api
        .get("/tickets", { params: { ...filters, from_date: fromDate, to_date: toDate, page, limit: 20 } })
        .then(({ data: result }) => active && setData(result))
        .catch(() => active && setData({ rows: [], totalRows: 0, pages: 1 }))
        .finally(() => active && setLoading(false));
    }, filters.search ? 400 : 0);
    return () => { active = false; clearTimeout(timer); };
  }, [filters, page, reload, fromDate, toDate, filialId]);

  async function downloadReport() {
    setDownloading(true);
    try {
      const { data: blob } = await api.get("/tickets/report/pdf", { params: { from_date: fromDate, to_date: toDate }, responseType: "blob" });
      saveAs(blob, `tickets-report-${fromDate}_${toDate}.pdf`);
    } catch {
      toast.error("Could not generate the report.");
    } finally {
      setDownloading(false);
    }
  }

  const total = SUMMARY.reduce((sum, status) => sum + (summary.status[status] || 0), 0);
  const pieData = SUMMARY.map((status) => ({ key: status, name: STATUS[status].label, value: summary.status[status] || 0 }));
  const title = me.holding ? "Tickets — Holding" : me.level ? `Tickets — Level ${me.level}` : me.approver ? "Tickets — Approver" : "Tickets — Mine";

  return (
    <div className="h-full bg-white flex flex-1 flex-col justify-start items-start rounded-tr-2xl px-4 overflow-hidden">
      <PageHeader>
        <Breadcrumbs currentPage={currentPage} />
        <div className="flex gap-2 items-center">
          <button type="button" className="flex items-center gap-2 text-xs px-3 py-2 rounded-md border border-mila_orange text-mila_orange hover:bg-orange-50" onClick={() => setOpened("new")}>
            <Plus size={14} /> New ticket
          </button>
          {(me.holding || me.level > 0 || me.approver) && <button type="button" disabled={downloading} title="PDF report for the selected period" className="flex items-center gap-2 text-xs px-3 py-2 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-50" onClick={downloadReport}>
            {downloading ? <Loader size={14} className="animate-spin" /> : <Download size={14} />} Report
          </button>}
          {me.holding && (
            <button type="button" className="flex items-center gap-2 text-xs px-3 py-2 rounded-md text-gray-500 hover:bg-gray-100" onClick={() => setOpened("defaults")}>
              <Settings2 size={14} /> Default approvers
            </button>
          )}
        </div>
      </PageHeader>

      <div className="w-full pt-3 pb-4 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">{title}</h3>
        <div className="flex flex-wrap items-center gap-6">
          <div style={{ width: 88, height: 88, flexShrink: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={total ? pieData : [{ key: "none", name: "No tickets", value: 1 }]}
                  cx="50%"
                  cy="50%"
                  innerRadius={24}
                  outerRadius={40}
                  paddingAngle={total ? 2 : 0}
                  dataKey="value"
                  onClick={(slice) => total && slice?.key && setFilter("status", filters.status === slice.key ? "" : slice.key)}
                  style={{ cursor: "pointer" }}
                >
                  {(total ? pieData : [{ key: "none" }]).map((entry) => (
                    <Cell key={entry.key} fill={PALETTE[entry.key] || "#e5e7eb"} opacity={filters.status && filters.status !== entry.key ? 0.35 : 1} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center divide-x divide-gray-200">
              <SmallStatCard label="Total" value={total} active={!filters.status} onClick={() => setFilter("status", "")} />
              {SUMMARY.map((status) => (
                <SmallStatCard
                  key={status}
                  label={STATUS[status].label}
                  value={summary.status[status] || 0}
                  color={PALETTE[status]}
                  active={filters.status === status}
                  onClick={() => setFilter("status", filters.status === status ? "" : status)}
                />
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Submitted from:</span>
                <input type="date" value={fromDate} onChange={(e) => { setPage(1); setFromDate(e.target.value); }} className={dateInput} />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">to</span>
                <input type="date" value={toDate} onChange={(e) => { setPage(1); setToDate(e.target.value); }} className={dateInput} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-1 w-full min-h-0 gap-4 pt-4">
        <div className="flex flex-1 flex-col min-w-0 gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 border rounded-md px-2 py-2 bg-white flex-1 min-w-[180px]">
              <Search size={14} color={C.textMid} />
              <input value={filters.search} onChange={(e) => setFilter("search", e.target.value)} placeholder="Search title or code" className="text-xs outline-none w-full" />
            </div>
            <select className={selectClass} value={filters.type} onChange={(e) => setFilter("type", e.target.value)}>
              <option value="">Bug + Feature</option>
              <option value="bug">Bug</option>
              <option value="feature">Feature</option>
            </select>
            <select className={selectClass} value={filters.module} onChange={(e) => setFilter("module", e.target.value)}>
              <option value="">All sections</option>
              {MODULES.map((m) => (
                <option key={m.value} value={m.value}>{m.label}{summary.module[m.value] ? ` (${summary.module[m.value]})` : ""}</option>
              ))}
            </select>
            {(me.holding || me.level > 0 || me.approver) && <select className={selectClass} value={filters.scope} onChange={(e) => setFilter("scope", e.target.value)}>
              {SCOPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>}
          </div>

          <div className="flex-1 min-h-0 overflow-auto border rounded-lg">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-50 text-gray-500 text-left">
                <tr>
                  <th className="px-3 py-2 w-8" />
                  <th className="px-3 py-2">Code</th>
                  <th className="px-3 py-2">Title</th>
                  <th className="px-3 py-2">Section</th>
                  <th className="px-3 py-2">Branch</th>
                  <th className="px-3 py-2">Status</th>
                  {me.level > 0 && <th className="px-3 py-2">Level</th>}
                  <th className="px-3 py-2">Votes</th>
                  <th className="px-3 py-2">Reporter</th>
                  <th className="px-3 py-2">Opened</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={me.level > 0 ? 10 : 9} className="py-10 text-center"><Loader size={16} className="inline animate-spin" color={C.orange} /></td></tr>
                )}
                {!loading && !data.rows.length && (
                  <tr><td colSpan={me.level > 0 ? 10 : 9} className="py-10 text-center text-gray-400">No tickets</td></tr>
                )}
                {!loading && data.rows.map((ticket) => (
                  <tr
                    key={ticket.id}
                    onClick={() => setOpened(ticket.id)}
                    className={`cursor-pointer border-t hover:bg-gray-50 ${ticket.unread ? "bg-orange-50 font-semibold" : ""}`}
                  >
                    <td className="px-3 py-2 relative">
                      {ticket.unread && <span title="Updated" className="absolute left-1 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-red-500" />}
                      {ticket.type === "bug" ? <Bug size={14} color={C.orange} /> : <Lightbulb size={14} color={C.orange} />}
                    </td>
                    <td className="px-3 py-2 font-semibold whitespace-nowrap" style={{ color: C.primary }}>{ticket.code}</td>
                    <td className="px-3 py-2 max-w-[260px] truncate">{ticket.title}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{moduleLabel(ticket.module)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{ticket.filial?.name || "-"}</td>
                    <td className="px-3 py-2"><StatusChip status={ticket.status} /></td>
                    {me.level > 0 && <td className="px-3 py-2 whitespace-nowrap">N{ticket.level}</td>}
                    <td className="px-3 py-2 whitespace-nowrap">{ticket.status === "pending_approval" ? `${ticket.progress.approved}/${ticket.progress.required}` : "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{ticket.reporter?.name}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500">{formatDate(ticket.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between text-xs text-gray-500 pb-3">
            <span>{data.totalRows} tickets</span>
            <div className="flex items-center gap-2">
              <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)} className="p-1 border rounded disabled:opacity-40"><ChevronLeft size={14} /></button>
              <span>{page} / {data.pages}</span>
              <button type="button" disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="p-1 border rounded disabled:opacity-40"><ChevronRight size={14} /></button>
            </div>
          </div>
        </div>
      </div>

      {opened && (
        <PreviewController Context={FullGridContext} allowTextAreaEnter>
          <PagePreview access={pageAccess} id={opened} />
        </PreviewController>
      )}
    </div>
  );
}
